import { format } from "date-fns";
import type {
  TemplateDateFilterValue,
  TemplateDatePreset,
} from "./templateDateFilterTypes";
import {
  createDefaultTemplateDateFilterLast6Months,
  getPresetLocalDateRange,
  summarizeTemplateDateFilterSelection,
} from "../pages/SendEmail/templateDateFilter";
import { buildMonthPk, monthPksInLocalRange } from "./auditMonthPk";
import { CUSTOM_FILTER_CHIP_ID } from "../components/Organismos/Table/FilteredTable.types";

export const AUDIT_LOG_DATE_QUERY_FIELDS = ["pk", "timestamp"] as const;

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function parseYyyyMmDd(s: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) {
    return null;
  }
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) {
    return null;
  }
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) {
    return null;
  }
  return { y, m: mo, d };
}

/** Default: first day of current local month through today (replaces legacy month input). */
export function createDefaultAuditLogDateFilter(): TemplateDateFilterValue {
  const today = startOfLocalDay(new Date());
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    templateDatePreset: "custom",
    templateDateFrom: format(firstOfMonth, "yyyy-MM-dd"),
    templateDateTo: format(today, "yyyy-MM-dd"),
  };
}

export function resolveAuditLogLocalRange(
  value: TemplateDateFilterValue,
): { from: Date; to: Date } | null {
  const preset = value.templateDatePreset;

  if (preset === "all") {
    return resolveAuditLogLocalRange(createDefaultTemplateDateFilterLast6Months());
  }

  if (preset !== "custom") {
    return getPresetLocalDateRange(
      preset as Exclude<TemplateDatePreset, "custom" | "all">,
    );
  }

  const fromTrim = (value.templateDateFrom ?? "").trim();
  const toTrim = (value.templateDateTo ?? "").trim();
  if (!fromTrim && !toTrim) {
    return null;
  }

  if (fromTrim && !toTrim) {
    const b = parseYyyyMmDd(fromTrim);
    if (!b) {
      return null;
    }
    const day = startOfLocalDay(new Date(b.y, b.m - 1, b.d));
    return { from: day, to: day };
  }

  if (!fromTrim && toTrim) {
    const b = parseYyyyMmDd(toTrim);
    if (!b) {
      return null;
    }
    const day = startOfLocalDay(new Date(b.y, b.m - 1, b.d));
    return { from: day, to: day };
  }

  const a = parseYyyyMmDd(fromTrim);
  const b = parseYyyyMmDd(toTrim);
  if (!a || !b) {
    return null;
  }

  let from = startOfLocalDay(new Date(a.y, a.m - 1, a.d));
  let to = startOfLocalDay(new Date(b.y, b.m - 1, b.d));
  if (from > to) {
    const swap = from;
    from = to;
    to = swap;
  }
  return { from, to };
}

export type FirmLogsDateField = "from" | "to";

/** Apply a single-day pick for firm logs; keeps From <= To by adjusting the other bound when needed. */
export function buildFirmLogsDateFilterValue(
  field: FirmLogsDateField,
  date: Date,
  current: TemplateDateFilterValue,
): TemplateDateFilterValue {
  const picked = format(startOfLocalDay(date), "yyyy-MM-dd");
  let from = (current.templateDateFrom ?? "").trim();
  let to = (current.templateDateTo ?? "").trim();

  if (field === "from") {
    from = picked;
    const toParts = parseYyyyMmDd(to);
    if (toParts) {
      const toDay = startOfLocalDay(
        new Date(toParts.y, toParts.m - 1, toParts.d),
      );
      if (toDay < startOfLocalDay(date)) {
        to = picked;
      }
    }
  } else {
    to = picked;
    const fromParts = parseYyyyMmDd(from);
    if (fromParts) {
      const fromDay = startOfLocalDay(
        new Date(fromParts.y, fromParts.m - 1, fromParts.d),
      );
      if (fromDay > startOfLocalDay(date)) {
        from = picked;
      }
    }
  }

  return {
    templateDatePreset: "custom",
    templateDateFrom: from,
    templateDateTo: to,
  };
}

export function clearFirmLogsDateField(
  field: FirmLogsDateField,
  current: TemplateDateFilterValue,
): TemplateDateFilterValue {
  return {
    templateDatePreset: "custom",
    templateDateFrom: field === "from" ? "" : (current.templateDateFrom ?? ""),
    templateDateTo: field === "to" ? "" : (current.templateDateTo ?? ""),
  };
}

export type AuditLogDateFilterClause = {
  pk: { value: string | string[]; comparisonType: "EQUAL" | "IN" };
  timestamp: Array<{
    value: string;
    comparisonType: "GREATER_THAN" | "LESS_THAN";
  }>;
};

/** Cosmos filter fragment: `pk` partition(s) + inclusive local `timestamp` bounds (ISO). */
export function buildAuditLogDateFilter(
  preset: TemplateDatePreset,
  dateFrom: string,
  dateTo: string,
): AuditLogDateFilterClause | undefined {
  const range = resolveAuditLogLocalRange({
    templateDatePreset: preset,
    templateDateFrom: dateFrom,
    templateDateTo: dateTo,
  });
  if (!range) {
    return undefined;
  }

  const pks = monthPksInLocalRange(range.from, range.to);
  if (pks.length === 0) {
    return undefined;
  }

  const start = new Date(
    range.from.getFullYear(),
    range.from.getMonth(),
    range.from.getDate(),
    0,
    0,
    0,
    0,
  );
  const end = new Date(
    range.to.getFullYear(),
    range.to.getMonth(),
    range.to.getDate(),
    23,
    59,
    59,
    999,
  );
  const startBound = new Date(start.getTime() - 1).toISOString();
  const endBound = new Date(end.getTime() + 1).toISOString();

  const pk =
    pks.length === 1
      ? { value: pks[0], comparisonType: "EQUAL" as const }
      : { value: pks, comparisonType: "IN" as const };

  return {
    pk,
    timestamp: [
      { value: startBound, comparisonType: "GREATER_THAN" },
      { value: endBound, comparisonType: "LESS_THAN" },
    ],
  };
}

export function auditLogDateFilterKey(value: TemplateDateFilterValue): string {
  const range = resolveAuditLogLocalRange(value);
  if (!range) {
    return buildMonthPk();
  }
  return `${format(range.from, "yyyy-MM-dd")}_${format(range.to, "yyyy-MM-dd")}`;
}

export function auditLogDateFilterChips(filterValue: unknown) {
  const label = summarizeTemplateDateFilterSelection(
    filterValue as TemplateDateFilterValue | null | undefined,
  );
  if (!label) {
    return [];
  }
  return [{ id: CUSTOM_FILTER_CHIP_ID, name: label }];
}

export function defaultAuditLogDateQueryClauses(): AuditLogDateFilterClause | null {
  const defaults = createDefaultAuditLogDateFilter();
  return (
    buildAuditLogDateFilter(
      defaults.templateDatePreset,
      defaults.templateDateFrom,
      defaults.templateDateTo,
    ) ?? null
  );
}
